import { roomName } from '../../../libs';
import { FloorZone } from '../types';

/**
 * Station names, as three different systems spell them, onto zones of a plan.
 *
 * `listStaffTracking` keys its per-station figures by a free string that the CV
 * job writes, and nothing constrains it to the `CookingStationName` enum KDS
 * uses elsewhere — records in the wild carry "Butchery" and "butchery" for the
 * same room. Matching is therefore done on a normalised form rather than by
 * equality, and an unmatched station is reported
 * as unmatched rather than dropped: a station nobody can place is a mapping bug
 * worth seeing, and silently discarding it would hide work the kitchen did.
 *
 * A camera's calibrated room, a data-platform room ID, takes the same route by its KDS name (`withCameras` in
 * `plan.ts`).
 *
 * The table is Bahrain's station list; other kitchens match on the zone names
 * in their own plan file, which is what the fallback below is for.
 */

const normalise = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Station spelling → zone id. Keys are normalised on the way in, so
 * "Garde Manger", "gardeManger" and "garde-manger" are the same entry.
 */
const STATION_ALIASES: Record<string, string> = {
  // CookingStationName, as KDS spells it elsewhere.
  vegetable: 'vegetables-section',
  hot: 'hot-section',
  boiling: 'hot-section',
  roasting: 'roasting-section',
  sauce: 'sauce-section',
  saucier: 'sauce-section',
  coldsauce: 'sauce-section',
  gardemanger: 'cold-section',
  coldpastry: 'bakery',
  bakeryandpastry: 'bakery',
  hotbakery: 'bakery',
  portioning: 'portioning-room',
  assembly: 'portioning-room',
  commissary: 'dry-store',
  merchandise: 'calo-2-0-portioning-room',

  // Rooms camera_rooms.json keeps apart that are one room on the floor: the
  // order-picking shelves are the west end of the portioning room.
  shoppingarea: 'portioning-room'
};

/** The zone a station name refers to, or null if nothing on this plan matches. */
export const zoneForStation = (zoneById: Map<string, FloorZone>, station: string): string | null => {
  const key = normalise(roomName(station));
  const direct = STATION_ALIASES[key];
  if (direct && zoneById.has(direct)) {
    return direct;
  }
  // Fall back to the zone names themselves, so a zone added to a plan file is
  // matchable without also being added to the table above.
  for (const [id, zone] of zoneById) {
    if (normalise(zone.name) === key || normalise(id) === key) {
      return id;
    }
  }
  return null;
};
